import { type CookieAttributes } from "node_modules/@types/js-cookie";
import cookies from "js-cookie";

const COOKIES_DISABLED_CLASS = "no-cookies";
const COOKIE_MAX_AGE_DAYS = 365; // 1 year

export const setCookie = (
  name: string,
  value: string,
  opts?: CookieAttributes,
): void => {
  if (!isCookieEnabled()) return;

  cookies.set(name, value, getOptions(opts));
};

export const getCookie = (name: string): string | undefined =>
  cookies.get(name);

export const removeCookie = (name: string, opts?: CookieAttributes): void => {
  if (!isCookieEnabled()) return;

  cookies.remove(name, getOptions(opts));
};

// local helpers

const isCookieEnabled = (): boolean => {
  try {
    if (document.body.classList.contains(COOKIES_DISABLED_CLASS)) {
      return false;
    }
  } catch {
    // ignore
  }

  return true;
};

let domain: string | undefined;

const getOptions = (opts?: CookieAttributes): CookieAttributes => {
  if (!domain) {
    domain = tld(window.location.href);
  }

  return {
    expires: COOKIE_MAX_AGE_DAYS,
    domain,
    path: "/",
    sameSite: "Lax",
    ...opts,
  };
};

/**
 * TLD returns the top level domain of the given url.
 * For example:
 * - www.google.com => google.com
 * - www.google.co.uk => google.co.uk
 * - localhost => undefined
 *
 * @source https://github.com/segmentio/analytics-next/
 */
function tld(url: string): string | undefined {
  const parsedUrl = parseUrl(url);
  if (!parsedUrl) return;

  const lvls = levels(parsedUrl);

  // Lookup the real top level one.
  for (let i = 0; i < lvls.length; ++i) {
    const cname = "__tld__";
    const domain = lvls[i];
    const opts = { domain: "." + domain };

    try {
      // cookie access throw an error if the library is ran inside a sandboxed environment (e.g. sandboxed iframe)
      cookies.set(cname, "1", opts);
      if (cookies.get(cname)) {
        cookies.remove(cname, opts);
        return domain;
      }
    } catch {
      return;
    }
  }
}

/**
 * Levels returns all levels of the given url.
 */
function levels(url: URL): string[] {
  const host = url.hostname;
  const parts = host.split(".");
  const last = parts[parts.length - 1];
  const levels: string[] = [];

  // Ip address.
  if (parts.length === 4 && parseInt(last!, 10) > 0) {
    return levels;
  }

  // Localhost.
  if (parts.length <= 1) {
    return levels;
  }

  // Create levels.
  for (let i = parts.length - 2; i >= 0; --i) {
    levels.push(parts.slice(i).join("."));
  }

  return levels;
}

function parseUrl(url: string): URL | undefined {
  try {
    return new URL(url);
  } catch {
    return;
  }
}
